import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { useState, type SyntheticEvent } from "react";

import type { OpsEmployeePageSize, OpsRoleBundle } from "../../shared/operations-schemas";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GrantEmployeeAccess } from "./GrantEmployeeAccess";
import { listEmployees } from "./api";
import { EmployeeResults } from "./EmployeeDirectory";

const PAGE_SIZES: OpsEmployeePageSize[] = [10, 25, 50];

function EmployeeSearch({ onSearch }: { onSearch: (query: string) => void }) {
  const [value, setValue] = useState("");
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    onSearch(value.trim());
  }
  return (
    <form className="relative w-full sm:w-96" onSubmit={submit} role="search">
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        aria-label="Find employees"
        className="pr-12 pl-9"
        id="employee-search"
        maxLength={100}
        onChange={(event) => {
          setValue(event.currentTarget.value);
          if (!event.currentTarget.value) onSearch("");
        }}
        placeholder="Find by name or email"
        type="search"
        value={value}
      />
      {value ? (
        <Button
          aria-label="Clear employee search"
          className="absolute top-1/2 right-1 -translate-y-1/2"
          onClick={() => {
            setValue("");
            onSearch("");
          }}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          <X />
        </Button>
      ) : null}
    </form>
  );
}

function EmployeeResultsToolbar({
  first,
  last,
  loading,
  pageSize,
  query,
  total,
  onPageSizeChange,
}: {
  first: number;
  last: number;
  loading: boolean;
  pageSize: OpsEmployeePageSize;
  query: string;
  total: number;
  onPageSizeChange: (pageSize: OpsEmployeePageSize) => void;
}) {
  return (
    <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <p aria-live="polite" className="text-sm text-muted-foreground tabular-nums">
        {loading
          ? "Loading employees…"
          : `Showing ${first}–${last} of ${total}${query ? ` for “${query}”` : ""}`}
      </p>
      <div className="flex items-center gap-2">
        <Label htmlFor="employee-page-size">Rows per page</Label>
        <select
          className="min-h-10 rounded-sm border border-input bg-card px-3 text-sm outline-none focus-visible:border-primary focus-visible:shadow-[var(--field-focus-shadow)]"
          id="employee-page-size"
          onChange={(event) => {
            onPageSizeChange(Number(event.currentTarget.value) as OpsEmployeePageSize);
          }}
          value={pageSize}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function EmployeePagination({
  fetching,
  page,
  total,
  totalPages,
  onPageChange,
}: {
  fetching: boolean;
  page: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (total === 0) return null;
  return (
    <nav aria-label="Employee pagination" className="mt-5 flex items-center justify-between gap-3">
      <p className="text-sm text-muted-foreground tabular-nums">
        Page {page} of {totalPages}
      </p>
      <div className="flex gap-2">
        <Button
          disabled={page <= 1 || fetching}
          onClick={() => {
            onPageChange(Math.max(1, page - 1));
          }}
          type="button"
          variant="outline"
        >
          <ChevronLeft /> Previous
        </Button>
        <Button
          disabled={page >= totalPages || fetching}
          onClick={() => {
            onPageChange(Math.min(totalPages, page + 1));
          }}
          type="button"
          variant="outline"
        >
          Next <ChevronRight />
        </Button>
      </div>
    </nav>
  );
}

/** The "showing X to Y" span for the current page, or zeroes when the page came back empty. */
function resultRange(page: number, pageSize: number, rowCount: number) {
  if (rowCount === 0) return { first: 0, last: 0 };
  const first = (page - 1) * pageSize + 1;
  return { first, last: first + rowCount - 1 };
}

function EmployeesHeader({ roleBundles }: { roleBundles: OpsRoleBundle[] }) {
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Employees</h1>
          <p className="mt-1 text-pretty text-muted-foreground">
            Who works in the operations console, and what each person can do.
          </p>
        </div>
        <GrantEmployeeAccess roleBundles={roleBundles} />
      </div>
    </>
  );
}

export function EmployeesView({ currentEmployeeId }: { currentEmployeeId: string }) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<OpsEmployeePageSize>(25);
  const employees = useQuery({
    queryKey: ["ops", "employees", { query, page, pageSize }],
    queryFn: () => listEmployees({ ...(query ? { query } : {}), page, pageSize }),
    placeholderData: keepPreviousData,
  });
  const rows = employees.data?.employees ?? [];
  const total = employees.data?.total ?? 0;
  const totalPages = employees.data?.totalPages ?? 1;
  const { first, last } = resultRange(page, pageSize, rows.length);
  return (
    <section>
      <EmployeesHeader roleBundles={employees.data?.roleBundles ?? []} />
      <div className="mt-6" />
      <EmployeeSearch
        onSearch={(nextQuery) => {
          setQuery(nextQuery);
          setPage(1);
        }}
      />
      {employees.error ? (
        <div className="mt-4">
          <FieldError>{employees.error.message}</FieldError>
        </div>
      ) : null}
      <EmployeeResultsToolbar
        first={first}
        last={last}
        loading={employees.isPending}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setPage(1);
        }}
        pageSize={pageSize}
        query={query}
        total={total}
      />
      <EmployeeResults
        currentEmployeeId={currentEmployeeId}
        fetching={employees.isFetching}
        loading={employees.isPending}
        query={query}
        roleBundles={employees.data?.roleBundles ?? []}
        rows={rows}
      />
      <EmployeePagination
        fetching={employees.isFetching}
        onPageChange={setPage}
        page={page}
        total={total}
        totalPages={totalPages}
      />
    </section>
  );
}
